/**
 * Platform-neutral transaction byte encoders.
 *
 * Browser and native runtimes cannot rely on Node's global `Buffer`.
 */

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const HEX_ALPHABET = '0123456789abcdef';

export function toHex(bytes: Uint8Array): string {
  let encoded = '';
  for (const byte of bytes) {
    encoded += HEX_ALPHABET.charAt(byte >>> 4) + HEX_ALPHABET.charAt(byte & 0x0f);
  }
  return encoded;
}

export function fromHex(encoded: string): Uint8Array {
  if (encoded.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(encoded)) {
    throw new TypeError('Expected an even-length hexadecimal string');
  }

  const bytes = new Uint8Array(encoded.length / 2);
  for (let index = 0; index < encoded.length; index += 2) {
    bytes[index / 2] = Number.parseInt(encoded.slice(index, index + 2), 16);
  }
  return bytes;
}

export function toBase64(bytes: Uint8Array): string {
  let encoded = '';
  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index];
    if (first === undefined) break;
    const second = bytes[index + 1];
    const third = bytes[index + 2];
    const secondValue = second ?? 0;
    const thirdValue = third ?? 0;

    encoded += BASE64_ALPHABET.charAt(first >>> 2);
    encoded += BASE64_ALPHABET.charAt(((first & 0x03) << 4) | (secondValue >>> 4));
    encoded +=
      second === undefined
        ? '='
        : BASE64_ALPHABET.charAt(((secondValue & 0x0f) << 2) | (thirdValue >>> 6));
    encoded += third === undefined ? '=' : BASE64_ALPHABET.charAt(thirdValue & 0x3f);
  }
  return encoded;
}

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

export function fromBase58(encoded: string): Uint8Array {
  let value = 0n;
  for (const char of encoded) {
    const digit = BASE58_ALPHABET.indexOf(char);
    if (digit < 0) throw new TypeError('Expected a base58 string');
    value = value * 58n + BigInt(digit);
  }
  const body: number[] = [];
  for (; value > 0n; value >>= 8n) body.unshift(Number(value & 0xffn));
  const leadingZeros = encoded.length - encoded.replace(/^1+/u, '').length;
  return Uint8Array.from([...new Array<number>(leadingZeros).fill(0), ...body]);
}
